import type { ThemeConfig } from 'antd';
import { DESIGN_COMPONENTS, DESIGN_TOKEN } from '@shared/providers/theme/designTokens';
import { HITOWA_BRAND } from '../../../constants/brand';

/**
 * Ant Design theme for the RAH shell.
 *
 * The RAH screens are built from antd components, but they must still read as
 * the ConceptDesign rather than as stock antd: dense rows, 12px type, small
 * radii, brand blue. This maps the BC-* tokens onto antd's token system so one
 * `ConfigProvider` restyles every control at once, instead of each component
 * carrying overrides.
 *
 * Grid density comes from the ConceptDesign's own CSS: `.data-table` is 12px
 * with 7px cell padding. Interactive controls do **not**: they keep RA2N's
 * scale, imported from `designTokens` rather than restated, so a button reads
 * the same here as on the screen it was inherited from. That matters because
 * the 利用者詳細 drawer mounts RA2N's own patient edit sections verbatim.
 */
export const hitowaAntdTheme: ThemeConfig = {
  token: {
    colorPrimary: HITOWA_BRAND.brand,
    colorLink: HITOWA_BRAND.brand,
    colorInfo: HITOWA_BRAND.brand,
    colorTextBase: HITOWA_BRAND.inkBody,
    colorBorder: HITOWA_BRAND.border,
    colorBgLayout: HITOWA_BRAND.canvas,

    fontFamily: '"Noto Sans JP", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 12,
    fontSizeSM: 11,
    fontSizeLG: 13,

    borderRadius: 5,
    borderRadiusSM: 3,
    borderRadiusLG: 8,

    // Controls follow RA2N, not the prototype — see the note above.
    controlHeight: DESIGN_TOKEN.controlHeight,
    controlHeightSM: DESIGN_TOKEN.controlHeightSM,
    controlHeightLG: DESIGN_TOKEN.controlHeightLG,
  },
  components: {
    Table: {
      headerBg: '#F1F3F7',
      headerColor: '#4A5670',
      headerSplitColor: 'transparent',
      rowHoverBg: HITOWA_BRAND.brandPale,
      rowSelectedBg: HITOWA_BRAND.brandLight,
      rowSelectedHoverBg: HITOWA_BRAND.brandLight,
      borderColor: '#E4E7EE',
      cellPaddingBlock: 7,
      cellPaddingInline: 10,
      cellFontSize: 12,
      headerBorderRadius: 0,
    },
    Button: {
      // Brand colour is Hitowa's; size and weight are RA2N's.
      colorPrimary: HITOWA_BRAND.brand,
      colorPrimaryHover: HITOWA_BRAND.brandHover,
      ...DESIGN_COMPONENTS.Button,
    },
    Modal: {
      titleFontSize: 14,
      headerBg: HITOWA_BRAND.brand,
      // Always set with `headerBg`, never alone. Overriding just the background
      // once left the title on RA2N's near-white `sectionHeaderText` over a
      // pale header — the 撮影依頼 title was invisible.
      titleColor: HITOWA_BRAND.surface,
      borderRadiusLG: 8,
    },
    Drawer: {
      footerPaddingBlock: 10,
      footerPaddingInline: 16,
    },
    Tabs: {
      itemColor: '#6E7280',
      itemSelectedColor: HITOWA_BRAND.brand,
      itemHoverColor: HITOWA_BRAND.brandHover,
      inkBarColor: HITOWA_BRAND.brand,
      titleFontSize: 13,
      horizontalItemPadding: '8px 16px',
      horizontalMargin: '0',
    },
    Select: { optionFontSize: 12, optionSelectedBg: HITOWA_BRAND.brandLight },
    Input: { paddingInline: 8 },
    Tag: { defaultBg: '#F1F3F7', defaultColor: '#4A5670', borderRadiusSM: 3 },
    Popover: { titleMinWidth: 0, borderRadiusLG: 8 },
    Checkbox: { controlInteractiveSize: 14 },
    Radio: { radioSize: 14, dotSize: 6 },
    Pagination: { itemSize: 28, itemSizeSM: 24 },
    Badge: { dotSize: 7 },
  },
};

export default hitowaAntdTheme;
